import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Loading } from 'components/patterns'
import {
  Modal,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalContent,
  ModalSideContainer,
  ModalFooter,
  ModalButton,
  ModalSizes,
} from 'components/patterns/Modal'
import SelectMenuBase from 'components/patterns/Inputs/SelectMenu'
import RadioGroup from 'components/patterns/RadioGroup'
import ChecIcon from 'components/patterns/Icon'
import { yup } from 'schemas/validation'
import featuresStrings from 'utils/strings/features-strings'
import { displayModuleName } from 'i18n/displayConstants'
import { getFastFeatureDetails } from 'services/fastFeatures'
import { saveFastFeatureDetails } from 'actions/feature/fastFeatures'
import {
  getFastFeatureIsSaving,
  getFeatureSwitchNumber,
} from 'selectors/feature/fastFeature'
import {
  OUTPUT_TYPES,
  INPUT_TYPES,
  OUTPUT_TYPE_OPTIONS,
  OUTPUT_TILE_LABELS,
  INPUT_TYPE_OPTIONS,
  OUTPUT_TYPE_TO_OPTIONS_KEY,
  buildInitialOutputs,
  MAX_OUTPUTS,
  CODES_35M,
  FILTER_OUTPUT_OPTIONS,
} from './constants'
import {
  ModalTitle,
  FeatureInfoPanel,
  FeatureInfoRow,
  FeatureInfoLabel,
  FeatureInfoValue,
  OutputSectionHeader,
  FieldGroup,
  Code35MRow,
  OutputTileList,
  OutputTile,
  OutputTileTitle,
  OutputTileSubtext,
  WarningIcon,
  FilterSection,
  FilterLabel,
  HorizontalRadioGroupWrapper,
  LoadErrorContainer,
} from './AssignFastFeatureDetailsModal.style'

const getModalPopupContainer = (trigger) =>
  trigger.closest('[data-modal-bounds]') || trigger.parentNode

const SelectMenu = (props) => (
  <SelectMenuBase getPopupContainer={getModalPopupContainer} {...props} />
)

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildDetailOptions = (outputType, availableOptions) => {
  if (!outputType || outputType === OUTPUT_TYPES.NA) return []
  const key = OUTPUT_TYPE_TO_OPTIONS_KEY[outputType]
  if (!key || !availableOptions[key]) return []
  return availableOptions[key].map(({ value, label }) => ({
    key: value,
    value,
    title: label,
  }))
}

const buildSwitchOptions = (availableOptions) => {
  if (!availableOptions.switches) return []
  return availableOptions.switches.map(({ value, label }) => ({
    key: value,
    value,
    title: label,
  }))
}

// Returns the options for a single 35M connector dropdown, filtered by the
// output's amp type.  apiOutputType ('2A'|'5A'|'20A'|'ECP') narrows to the
// exact type when available; outputType ('PIN'|'ECP') is the fallback.
const build35MOptions = (
  connectorCode,
  apiOutputType,
  outputType,
  availableOptions
) => {
  const naOption = { key: 'na', value: 'na', title: 'N/A' }
  if (!outputType || outputType === OUTPUT_TYPES.NA) return [naOption]

  const connectorMap = availableOptions.codes35MByConnector
  if (!connectorMap) return [naOption]
  const connectorData = connectorMap[connectorCode]
  if (!connectorData) return [naOption]

  const toSelectOptions = (list) =>
    (list || []).map(({ value, label }) => ({
      key: value,
      value,
      title: label.split(' ')[0],
    }))

  // ECP type: show ECP options regardless of apiOutputType
  if (outputType === OUTPUT_TYPES.ECP) {
    return [naOption, ...toSelectOptions(connectorData.ECP)]
  }

  // Switch Indicator: no 35M codes apply
  if (outputType === OUTPUT_TYPES.SWITCH_INDICATOR) {
    return [naOption]
  }

  // PIN type: narrow by apiOutputType when known
  if (apiOutputType && apiOutputType !== 'SWI') {
    return [naOption, ...toSelectOptions(connectorData[apiOutputType])]
  }

  // PIN type without a known sub-type: show all pin options combined
  return [
    naOption,
    ...toSelectOptions(connectorData['20A']),
    ...toSelectOptions(connectorData['5A']),
    ...toSelectOptions(connectorData['2A']),
  ]
}

const isOutputConfigured = (output) =>
  output.outputType !== null &&
  output.outputType !== OUTPUT_TYPES.NA &&
  output.outputDetail !== null

// ─── Feature Info Panel ───────────────────────────────────────────────────────

const FeatureInfo = ({ featureDescription, minDiagVariant, ecu }) => (
  <FeatureInfoPanel>
    <FeatureInfoRow>
      <FeatureInfoLabel>
        {featuresStrings.ff_feature_description_label}:
      </FeatureInfoLabel>
      <FeatureInfoValue>{featureDescription || '—'}</FeatureInfoValue>
    </FeatureInfoRow>
    <FeatureInfoRow>
      <FeatureInfoLabel>
        {featuresStrings.ff_min_diag_variant_label}:
      </FeatureInfoLabel>
      <FeatureInfoValue>{minDiagVariant || '—'}</FeatureInfoValue>
    </FeatureInfoRow>
    <FeatureInfoRow>
      <FeatureInfoLabel>{featuresStrings.ff_ecu_label}:</FeatureInfoLabel>
      <FeatureInfoValue>
        {displayModuleName(ecu) || ecu || '—'}
      </FeatureInfoValue>
    </FeatureInfoRow>
  </FeatureInfoPanel>
)

FeatureInfo.propTypes = {
  featureDescription: PropTypes.string,
  minDiagVariant: PropTypes.string,
  ecu: PropTypes.string,
}

FeatureInfo.defaultProps = {
  featureDescription: '',
  minDiagVariant: '',
  ecu: '',
}

// ─── Output Form (main content for a selected tile) ───────────────────────────

const OutputForm = ({ output, availableOptions, onChange }) => {
  const {
    outputNumber,
    outputType,
    outputDetail,
    inputType,
    inputDetail,
    codes35M,
  } = output

  const detailOptions = buildDetailOptions(outputType, availableOptions)
  const switchOptions = buildSwitchOptions(availableOptions)

  const isNAOutput = outputType === OUTPUT_TYPES.NA
  const isNAInput = inputType === INPUT_TYPES.NA
  const isOutputDetailDisabled = !outputType || isNAOutput
  const isInputDetailDisabled = !inputType || isNAInput

  const handleChange = (field, value) => {
    const updated = { ...output, [field]: value }
    if (field === 'outputType') {
      updated.outputDetail = null
    }
    if (field === 'inputType' && value === INPUT_TYPES.NA) {
      updated.inputDetail = null
    }
    onChange(outputNumber, updated)
  }

  const handleCode35MChange = (code, value) => {
    onChange(outputNumber, {
      ...output,
      codes35M: { ...codes35M, [code]: value },
    })
  }

  return (
    <>
      <OutputSectionHeader>
        {featuresStrings.ff_output_label(outputNumber)}
      </OutputSectionHeader>

      <FieldGroup>
        <SelectMenu
          label={featuresStrings.ff_output_type}
          name={`output-type-${outputNumber}`}
          onChange={(val) => handleChange('outputType', val)}
          options={OUTPUT_TYPE_OPTIONS}
          value={outputType}
          rules={yup.string().nullable()}
          placeholder="Select"
        />
        <SelectMenu
          label={featuresStrings.ff_output_details}
          name={`output-detail-${outputNumber}`}
          onChange={(val) => handleChange('outputDetail', val)}
          options={detailOptions}
          value={isOutputDetailDisabled ? null : outputDetail}
          rules={yup.string().nullable()}
          disabled={isOutputDetailDisabled}
          placeholder="Select"
        />
      </FieldGroup>

      <Code35MRow>
        {CODES_35M.map((code) => {
          const codeOptions = build35MOptions(
            code,
            output.apiOutputType,
            outputType,
            availableOptions
          )
          return (
            <SelectMenu
              key={code}
              label={code}
              name={`code-35m-${code}-${outputNumber}`}
              onChange={(val) => handleCode35MChange(code, val)}
              options={codeOptions}
              value={codes35M?.[code] ?? null}
              rules={yup.string().nullable()}
              placeholder="N/A"
            />
          )
        })}
      </Code35MRow>

      <FieldGroup>
        <SelectMenu
          label={featuresStrings.ff_input_type}
          name={`input-type-${outputNumber}`}
          onChange={(val) => handleChange('inputType', val)}
          options={INPUT_TYPE_OPTIONS}
          value={inputType}
          rules={yup.string().nullable()}
          placeholder="N/A"
        />
        <SelectMenu
          label={featuresStrings.ff_input_details}
          name={`input-detail-${outputNumber}`}
          onChange={(val) => handleChange('inputDetail', val)}
          options={switchOptions}
          value={isInputDetailDisabled ? null : inputDetail}
          rules={yup.string().nullable()}
          disabled={isInputDetailDisabled}
          placeholder={
            isInputDetailDisabled ? 'N/A' : featuresStrings.select_criteria
          }
        />
      </FieldGroup>
    </>
  )
}

OutputForm.propTypes = {
  output: PropTypes.shape({
    outputNumber: PropTypes.number,
    outputType: PropTypes.string,
    outputDetail: PropTypes.string,
    inputType: PropTypes.string,
    inputDetail: PropTypes.string,
    codes35M: PropTypes.object,
    apiOutputType: PropTypes.string,
  }).isRequired,
  availableOptions: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
}

// ─── Output Tile (side panel) ─────────────────────────────────────────────────

const OutputTileItem = ({ output, selected, onClick }) => {
  const { outputNumber, outputType } = output
  const configured = isOutputConfigured(output)
  const typeLabel = OUTPUT_TILE_LABELS[outputType] || null

  return (
    <OutputTile
      type="button"
      selected={selected}
      onClick={() => onClick(outputNumber - 1)}
    >
      <OutputTileTitle>
        {featuresStrings.ff_output_tile_label(outputNumber)}
      </OutputTileTitle>
      <OutputTileSubtext configured={configured}>
        {configured ? (
          <ChecIcon
            icon="check-mark-only"
            fill="var(--Banner-Notifications-Banner-Confirm-Green-Primary, #30A621)"
            size={14}
          />
        ) : (
          <ChecIcon
            icon="alert_info_i_round_solid"
            fill="var(--Slate-Dark-Slate, rgba(79, 87, 100, 0.87))"
            size={20}
          />
        )}
        {typeLabel || featuresStrings.ff_not_selected}
      </OutputTileSubtext>
    </OutputTile>
  )
}

OutputTileItem.propTypes = {
  output: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
}

const FastFeatureFilterTileItem = ({
  selected,
  onClick,
  configured,
  disabled,
}) => (
  <OutputTile
    type="button"
    selected={selected}
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    style={disabled ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
  >
    <OutputTileTitle>{featuresStrings.ff_fast_feature_filter}</OutputTileTitle>
    <OutputTileSubtext configured={configured}>
      {configured ? (
        <ChecIcon
          icon="check-mark-only"
          fill="var(--Banner-Notifications-Banner-Confirm-Green-Primary, #30A621)"
          size={20}
        />
      ) : (
        <WarningIcon
          icon="warning_solid_black"
          fill="warningPrimary"
          size={14}
        />
      )}
      {configured
        ? featuresStrings.ff_selected
        : featuresStrings.ff_not_selected}
    </OutputTileSubtext>
  </OutputTile>
)

FastFeatureFilterTileItem.propTypes = {
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  configured: PropTypes.bool,
  disabled: PropTypes.bool,
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

const AssignFastFeatureDetailsModal = ({
  featureId,
  featureNumber,
  revision,
  featureDescription,
  minDiagVariant,
  ecu,
  onClose,
}) => {
  const dispatch = useDispatch()
  const isSaving = useSelector(getFastFeatureIsSaving)
  const featureSwitchNumber = useSelector(getFeatureSwitchNumber)
  const [saveStarted, setSaveStarted] = useState(false)
  const saveLockRef = useRef(false)
  const [selectedTile, setSelectedTile] = useState(0)
  const [outputs, setOutputs] = useState(buildInitialOutputs(MAX_OUTPUTS))
  const [filterOutput, setFilterOutput] = useState(null)
  const [availableOptions, setAvailableOptions] = useState({
    pins: [],
    solenoids: [],
    indicators: [],
    switches: [],
    codes35M: [],
  })
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [retryCount, setRetryCount] = useState(0)

  // Load details on mount (retryCount dependency allows explicit retry)
  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      setLoadError(false)
      try {
        const response = await getFastFeatureDetails({ featureId })
        const {
          outputs: loadedOutputs,
          availableOptions: opts,
          fastFeatureFilter: loadedFilter,
        } = response.data
        if (loadedOutputs && loadedOutputs.length) {
          // Pre-populate input from the feature's own switch config when
          // the fast feature metadata hasn't been explicitly saved yet.
          const hasInputConfigured = loadedOutputs.some(
            (o) => o.inputType && o.inputType !== INPUT_TYPES.NA
          )
          if (!hasInputConfigured && featureSwitchNumber != null) {
            const swDetail = `tem_switch_${String(featureSwitchNumber).padStart(
              2,
              '0'
            )}`
            setOutputs(
              loadedOutputs.map((o) => ({
                ...o,
                inputType: INPUT_TYPES.SWITCH,
                inputDetail: swDetail,
              }))
            )
          } else {
            setOutputs(loadedOutputs)
          }
        }
        if (opts) setAvailableOptions(opts)
        if (loadedFilter !== undefined) setFilterOutput(loadedFilter)
      } catch (err) {
        setLoadError(true)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [featureId, retryCount, featureSwitchNumber])

  const handleRetry = () => setRetryCount((c) => c + 1)

  // Close the modal once the saga finishes saving (isSaving goes false)
  useEffect(() => {
    if (saveStarted && !isSaving) {
      onClose()
    }
  }, [isSaving, saveStarted, onClose])

  const handleOutputChange = useCallback((outputNumber, updatedOutput) => {
    setOutputs((prev) =>
      prev.map((o) => (o.outputNumber === outputNumber ? updatedOutput : o))
    )
  }, [])

  const hasConfiguredOutput = outputs.some(
    (o) => o.outputType && o.outputType !== OUTPUT_TYPES.NA
  )

  const handleConfirm = () => {
    if (saveLockRef.current) return
    saveLockRef.current = true
    setSaveStarted(true)
    const effectiveFilter = hasConfiguredOutput ? filterOutput : null
    dispatch(
      saveFastFeatureDetails({
        featureId,
        featureNumber,
        description: featureDescription,
        ecuType: ecu,
        outputs,
        filterOutput: effectiveFilter,
      })
    )
  }

  const selectedOutput =
    selectedTile < MAX_OUTPUTS ? outputs[selectedTile] : null

  const isConfirmDisabled =
    isSaving ||
    isLoading ||
    loadError ||
    (selectedOutput !== null &&
      (!selectedOutput.outputType ||
        (selectedOutput.outputType !== OUTPUT_TYPES.NA &&
          !selectedOutput.outputDetail)))

  const title = featuresStrings.assign_fast_feature_details(
    featureNumber,
    revision
  )

  return (
    <Modal size={ModalSizes.XL}>
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
        <ModalCloseButton onClick={onClose} />
      </ModalHeader>

      <ModalBody>
        <ModalContent style={{ overflow: 'hidden' }}>
          {isLoading ? (
            <Loading contained size="sm" />
          ) : loadError ? (
            <LoadErrorContainer>
              <span>{featuresStrings.ff_load_error}</span>
              <ModalButton type="secondary" onClick={handleRetry}>
                {featuresStrings.ff_retry}
              </ModalButton>
            </LoadErrorContainer>
          ) : (
            <>
              <FeatureInfo
                featureDescription={featureDescription}
                minDiagVariant={minDiagVariant}
                ecu={ecu}
              />
              {selectedTile < MAX_OUTPUTS && selectedOutput && (
                <OutputForm
                  output={selectedOutput}
                  availableOptions={availableOptions}
                  onChange={handleOutputChange}
                />
              )}
              {selectedTile === MAX_OUTPUTS && (
                <>
                  <OutputSectionHeader>
                    {featuresStrings.ff_filters}
                  </OutputSectionHeader>
                  <FilterSection>
                    <FilterLabel>
                      {featuresStrings.ff_output_filter_label}
                    </FilterLabel>
                    <HorizontalRadioGroupWrapper>
                      <RadioGroup
                        horizontal
                        value={filterOutput}
                        onChange={(e) => setFilterOutput(e.target.value)}
                        options={FILTER_OUTPUT_OPTIONS.map(
                          ({ value, label }) => ({
                            key: value,
                            value,
                            text: label,
                          })
                        )}
                      />
                    </HorizontalRadioGroupWrapper>
                  </FilterSection>
                </>
              )}
            </>
          )}
        </ModalContent>

        <ModalSideContainer>
          <OutputTileList>
            {outputs.map((output, idx) => (
              <OutputTileItem
                key={output.outputNumber}
                output={output}
                selected={idx === selectedTile}
                onClick={setSelectedTile}
              />
            ))}
            <FastFeatureFilterTileItem
              selected={selectedTile === MAX_OUTPUTS}
              onClick={() => setSelectedTile(MAX_OUTPUTS)}
              configured={!!filterOutput}
              disabled={!hasConfiguredOutput}
            />
          </OutputTileList>
        </ModalSideContainer>
      </ModalBody>

      <ModalFooter>
        <ModalButton type="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </ModalButton>
        <ModalButton
          type="primary"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        >
          {featuresStrings.ff_confirm}
        </ModalButton>
      </ModalFooter>
    </Modal>
  )
}

AssignFastFeatureDetailsModal.propTypes = {
  featureId: PropTypes.string.isRequired,
  featureNumber: PropTypes.string,
  revision: PropTypes.string,
  featureDescription: PropTypes.string,
  minDiagVariant: PropTypes.string,
  ecu: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}

AssignFastFeatureDetailsModal.defaultProps = {
  featureNumber: '-',
  revision: '-',
  featureDescription: '',
  minDiagVariant: '',
  ecu: '',
}

export default AssignFastFeatureDetailsModal
